import React, { useState, useEffect } from 'react';
import { Bookmark, LayoutList, ChevronRight, ChevronDown, PenLine, Trash2, ArrowUp, ArrowDown, Loader2, ArrowLeft, Save, X, Check, Globe, Lock, Copy, Library, User, Calendar, Search, BookOpen, Share2, Eye } from 'lucide-react';
import { useQuran } from '../context/QuranContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import LoginPromptDialog from './LoginPromptDialog';
import { toast } from 'sonner';

const TopicManager = () => {
    const { topics, getTopicWithVerses, reorderTopicVerses, removeVerseFromTopic, updateVerseComment, updateTopic, goToVerse, startRootBrowsing, surahs, deleteTopic, publishTopic, getLibraryTopics, getPublicTopic, clonePublicTopic } = useQuran();
    const { isAuthenticated } = useAuth();
    
    const [isOpen, setIsOpen] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    
    // Tab state: 'my' | 'library'
    const [activeTab, setActiveTab] = useState('my');
    
    // View state: 'list' | 'detail' | 'library-detail'
    const [view, setView] = useState('list');
    
    // Detail state
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [topicVerses, setTopicVerses] = useState([]);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [isReordering, setIsReordering] = useState(false);

    // Comment expand/edit state
    const [expandedComments, setExpandedComments] = useState({});
    const [editingVerseId, setEditingVerseId] = useState(null);
    const [editComment, setEditComment] = useState('');
    const [isSavingComment, setIsSavingComment] = useState(false);

    // Topic rename state
    const [renamingTopicId, setRenamingTopicId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [isSavingRename, setIsSavingRename] = useState(false);

    // Library state
    const [libraryTopics, setLibraryTopics] = useState([]);
    const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [librarySearch, setLibrarySearch] = useState('');

    // Load library when tab switches to library
    useEffect(() => {
        if (activeTab === 'library' && isOpen) {
            loadLibrary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, isOpen]);

    const loadLibrary = async () => {
        setIsLoadingLibrary(true);
        try {
            const topics = await getLibraryTopics();
            setLibraryTopics(topics);
        } finally {
            setIsLoadingLibrary(false);
        }
    };

    const filteredLibraryTopics = libraryTopics.filter(t =>
        t.name.toLowerCase().includes(librarySearch.toLowerCase())
    );

    const loadTopicDetails = async (topic) => {
        setIsLoadingDetails(true);
        setSelectedTopic(topic);
        setView('detail');
        setExpandedComments({});
        setEditingVerseId(null);
        try {
            const data = await getTopicWithVerses(topic.id);
            if (data) {
                setTopicVerses(data.verses || []);
            }
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const loadLibraryTopicDetails = async (topic) => {
        setIsLoadingDetails(true);
        setSelectedTopic(topic);
        setView('library-detail');
        setExpandedComments({});
        try {
            const data = await getPublicTopic(topic.id);
            if (data) {
                setTopicVerses(data.verses || []);
            }
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const handleBackToList = () => {
        setView('list');
        setSelectedTopic(null);
        setTopicVerses([]);
        setExpandedComments({});
        setEditingVerseId(null);
        setRenamingTopicId(null);
    };

    const handleMoveVerse = async (index, direction) => {
        if (isReordering) return;
        const newVerses = [...topicVerses];
        if (direction === 'up' && index > 0) {
            [newVerses[index - 1], newVerses[index]] = [newVerses[index], newVerses[index - 1]];
        } else if (direction === 'down' && index < newVerses.length - 1) {
            [newVerses[index + 1], newVerses[index]] = [newVerses[index], newVerses[index + 1]];
        } else return;
        setTopicVerses(newVerses);
        setIsReordering(true);
        try {
            const verseIds = newVerses.map(v => v.id);
            await reorderTopicVerses(selectedTopic.id, verseIds);
        } catch {
            toast.error("Failed to save new order");
            const data = await getTopicWithVerses(selectedTopic.id);
            if (data) setTopicVerses(data.verses);
        } finally { setIsReordering(false); }
    };

    const handleRemoveVerse = async (verseId) => {
        setTopicVerses(prev => prev.filter(v => v.id !== verseId));
        await removeVerseFromTopic(selectedTopic.id, verseId);
        toast.success("Verse removed");
    };

    const handleDeleteTopic = async (topicId) => {
        await deleteTopic(topicId);
        toast.success("Topic deleted");
        if (view === 'detail' && selectedTopic?.id === topicId) handleBackToList();
    };

    const handlePublishToggle = async (e, topic) => {
        e.stopPropagation();
        const result = await publishTopic(topic.id);
        if (result) {
            toast.success(result.is_public ? "Topic published to Library" : "Topic unpublished");
        } else {
            toast.error("Failed to update publish status");
        }
    };

    const handleCloneTopic = async (topicId) => {
        if (!isAuthenticated) {
            setShowLoginPrompt(true);
            return;
        }
        setIsCloning(true);
        try {
            const result = await clonePublicTopic(topicId);
            if (result) {
                toast.success(result.message || "Topic added to your collection");
            } else {
                toast.error("Failed to clone topic");
            }
        } finally {
            setIsCloning(false);
        }
    };

    // Topic rename
    const startRenameTopic = (e, topic) => { e.stopPropagation(); setRenamingTopicId(topic.id); setRenameValue(topic.name); };
    const cancelRenameTopic = () => { setRenamingTopicId(null); setRenameValue(''); };
    const saveRenameTopic = async (e, topicId) => {
        e.stopPropagation();
        if (!renameValue.trim()) return;
        setIsSavingRename(true);
        try {
            const ok = await updateTopic(topicId, renameValue.trim());
            if (ok) { toast.success("Topic renamed"); cancelRenameTopic(); }
            else toast.error("Failed to rename topic");
        } finally { setIsSavingRename(false); }
    };

    const getSurahName = (verseKey) => {
        const surahId = parseInt(verseKey.split(':')[0]);
        const surah = surahs.find(s => s.id === surahId);
        return surah ? surah.name_simple : '';
    };
    const toggleExpand = (verseId) => setExpandedComments(prev => ({ ...prev, [verseId]: !prev[verseId] }));
    const startEditComment = (verse) => { setEditingVerseId(verse.id); setEditComment(verse.comment || ''); };
    const cancelEditComment = () => { setEditingVerseId(null); setEditComment(''); };
    const saveComment = async (verseId) => {
        setIsSavingComment(true);
        try {
            const ok = await updateVerseComment(selectedTopic.id, verseId, editComment);
            if (ok) { setTopicVerses(prev => prev.map(v => v.id === verseId ? { ...v, comment: editComment } : v)); cancelEditComment(); toast.success("Comment updated"); }
            else toast.error("Failed to update comment");
        } finally { setIsSavingComment(false); }
    };

    const handleNavigateToTopic = (startIndex = 0) => {
        if (!selectedTopic || topicVerses.length === 0) { toast.info("This topic has no verses yet."); return; }
        const mappedVerses = topicVerses.map(v => ({ verse_key: v.verse_key, topic_comment: v.comment }));
        const pseudoWord = { text_uthmani: selectedTopic.name, translation: { text: 'Topic' } };
        const targetVerse = mappedVerses[startIndex];
        const [surahId, verseNum] = targetVerse.verse_key.split(':').map(Number);
        startRootBrowsing(pseudoWord, mappedVerses, startIndex, 'topic');
        goToVerse(surahId, verseNum);
        setIsOpen(false);
        setTimeout(() => handleBackToList(), 500);
    };

    // Render verse card (reused in both detail views)
    const renderVerseCard = (verse, index, isReadOnly = false) => (
        <div key={verse.id || index} className="p-3 rounded-lg border border-[#1e293b] bg-[#09090b]/50 flex gap-3 group">
            {!isReadOnly && (
                <div className="flex flex-col gap-1 pt-1 opacity-100 sm:opacity-30 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleMoveVerse(index, 'up')} disabled={index === 0 || isReordering} className="p-1 rounded text-[#94a3b8] hover:text-white hover:bg-[#1e293b] disabled:opacity-20"><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => handleMoveVerse(index, 'down')} disabled={index === topicVerses.length - 1 || isReordering} className="p-1 rounded text-[#94a3b8] hover:text-white hover:bg-[#1e293b] disabled:opacity-20"><ArrowDown className="w-4 h-4" /></button>
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex gap-2 items-center mb-1 cursor-pointer" onClick={() => handleNavigateToTopic(index)}>
                    <span className="px-2 py-0.5 text-xs rounded bg-[#10b981]/20 text-[#10b981] font-medium">{getSurahName(verse.verse_key)}</span>
                    <span className="text-[#94a3b8] text-sm">{verse.verse_key}</span>
                </div>
                {!isReadOnly && editingVerseId === verse.id ? (
                    <div className="mt-2 space-y-2">
                        <Textarea value={editComment} onChange={(e) => setEditComment(e.target.value)} className="bg-[#09090b] border-[#334155] text-[#f8fafc] text-sm resize-none" rows={4} autoFocus />
                        <div className="flex gap-2 justify-end">
                            <Button variant="ghost" size="sm" onClick={cancelEditComment} className="text-[#94a3b8] h-7 text-xs"><X className="w-3 h-3 mr-1" /> Cancel</Button>
                            <Button size="sm" onClick={() => saveComment(verse.id)} disabled={isSavingComment} className="bg-[#10b981] hover:bg-[#059669] text-white h-7 text-xs">{isSavingComment ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}Save</Button>
                        </div>
                    </div>
                ) : verse.comment ? (
                    <div className="mt-2">
                        <div className="text-sm text-[#e2e8f0] bg-[#1e293b]/50 p-2 rounded italic border-l-2 border-[#10b981] cursor-pointer" onClick={() => toggleExpand(verse.id || index)}>
                            {expandedComments[verse.id || index] ? (
                                <pre className="whitespace-pre-wrap font-sans text-sm">{verse.comment}</pre>
                            ) : (
                                <p className="line-clamp-2">{verse.comment}</p>
                            )}
                            {verse.comment.length > 100 && (
                                <button className="text-[#10b981] text-xs mt-1 flex items-center gap-1 not-italic">
                                    <ChevronDown className={`w-3 h-3 transition-transform ${expandedComments[verse.id || index] ? 'rotate-180' : ''}`} />
                                    {expandedComments[verse.id || index] ? 'Show less' : 'Show more'}
                                </button>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
            {!isReadOnly && (
                <div className="flex flex-col gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" className="text-[#94a3b8] hover:text-[#10b981] hover:bg-[#10b981]/10 h-8 w-8 p-0" onClick={() => startEditComment(verse)} title="Edit comment"><PenLine className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-[#94a3b8] hover:text-[#ef4444] hover:bg-[#ef4444]/10 h-8 w-8 p-0" onClick={() => handleRemoveVerse(verse.id)} title="Remove from topic"><Trash2 className="w-4 h-4" /></Button>
                </div>
            )}
        </div>
    );

    // When not authenticated, open dialog directly to Library tab
    const handleTopicButtonClick = (e) => {
        if (!isAuthenticated) {
            // Allow dialog to open, but default to library tab
            setActiveTab('library');
        }
    };

    return (
        <>
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) { handleBackToList(); setActiveTab(isAuthenticated ? 'my' : 'library'); setLibrarySearch(''); } }}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10 hover:text-[#34d399] gap-2" data-testid="topic-manager-btn"
                    onClick={handleTopicButtonClick}
                >
                    <LayoutList className="w-4 h-4" />
                    <span className="hidden sm:inline">Topics</span>
                </Button>
            </DialogTrigger>

            <DialogContent className="bg-[#0f172a] border-[#1e293b] text-[#f8fafc] max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
                <DialogHeader className="p-4 sm:p-6 pb-0 flex-shrink-0 text-left">
                    <DialogTitle className="flex items-center gap-2 text-[#f8fafc]">
                        {view === 'list' ? (
                            <>
                                <LayoutList className="w-5 h-5 text-[#10b981]" />
                                Topics
                            </>
                        ) : (
                            <div className="w-full">
                                <div className="flex items-center gap-3">
                                    <Button variant="ghost" size="sm" className="p-0 h-8 w-8 text-[#94a3b8] hover:text-white rounded-full" onClick={handleBackToList}><ArrowLeft className="w-4 h-4" /></Button>
                                    <Bookmark className="w-5 h-5 text-[#10b981] ml-1 flex-shrink-0" />
                                    <span className="flex-1 truncate">{selectedTopic?.name}</span>
                                </div>
                                {(view === 'library-detail' || view === 'detail') && (selectedTopic?.author_name || selectedTopic?.published_at) && (
                                    <div className="flex items-center gap-2 text-xs text-[#94a3b8] font-normal ml-[4.25rem] mt-0.5">
                                        {selectedTopic?.author_name && <span>by {selectedTopic.author_name}</span>}
                                        {selectedTopic?.published_at && <span>• {new Date(selectedTopic.published_at).toLocaleDateString()}</span>}
                                    </div>
                                )}
                            </div>
                        )}
                    </DialogTitle>
                    
                    {/* Tabs — only on list view */}
                    {view === 'list' && (
                        <div className="flex border-b border-[#1e293b] mt-4">
                            <button
                                onClick={() => setActiveTab('my')}
                                className={`flex-1 pb-3 text-sm font-medium transition-colors border-b-2 ${
                                    activeTab === 'my' ? 'border-[#10b981] text-[#10b981]' : 'border-transparent text-[#94a3b8] hover:text-white'
                                }`}
                            >
                                Your Topics
                            </button>
                            <button
                                onClick={() => setActiveTab('library')}
                                className={`flex-1 pb-3 text-sm font-medium transition-colors border-b-2 flex items-center justify-center gap-2 ${
                                    activeTab === 'library' ? 'border-[#8b5cf6] text-[#8b5cf6]' : 'border-transparent text-[#94a3b8] hover:text-white'
                                }`}
                            >
                                <Library className="w-4 h-4" /> Library
                            </button>
                        </div>
                    )}
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-3 sm:pt-4">
                    {/* YOUR TOPICS TAB */}
                    {view === 'list' && activeTab === 'my' && (
                        <div className="space-y-4">
                            {!isAuthenticated ? (
                                // Not logged in — show auth required
                                <div className="flex flex-col items-center justify-center p-8 text-center text-[#94a3b8] border border-[#1e293b] rounded-md bg-[#09090b]/50">
                                    <Lock className="w-12 h-12 mb-4 opacity-30 text-[#d4af37]" />
                                    <p className="text-[#f8fafc] font-medium mb-1">Authentication Required</p>
                                    <p className="text-sm">Sign in to create and manage your personal topics.</p>
                                    <p className="text-sm mt-2">Browse the <button onClick={() => setActiveTab('library')} className="text-[#8b5cf6] hover:underline font-medium">Library</button> to explore published topics.</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-sm text-[#94a3b8]">Group verses into topics and tell a story by arranging them in order.</p>
                                    <div className="border border-[#1e293b] rounded-md bg-[#09090b]/50">
                                        {topics.length > 0 ? (
                                            <div className="divide-y divide-[#1e293b]">
                                                {topics.map(topic => (
                                                    <div key={topic.id} className="group flex flex-col sm:flex-row sm:items-center justify-between p-4 hover:bg-[#1e293b] transition-colors cursor-pointer"
                                                        onClick={() => { if (renamingTopicId !== topic.id) loadTopicDetails(topic); }}
                                                    >
                                                        <div className="flex-1 min-w-0 pr-4">
                                                            {renamingTopicId === topic.id ? (
                                                                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                                                    <Input value={renameValue} onChange={e => setRenameValue(e.target.value)} className="bg-[#09090b] border-[#334155] text-white h-8 text-sm" autoFocus
                                                                        onKeyDown={e => { if (e.key === 'Enter') saveRenameTopic(e, topic.id); if (e.key === 'Escape') cancelRenameTopic(); }} />
                                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#10b981]" onClick={e => saveRenameTopic(e, topic.id)} disabled={isSavingRename}>
                                                                        {isSavingRename ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                                                    </Button>
                                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#94a3b8]" onClick={e => { e.stopPropagation(); cancelRenameTopic(); }}><X className="w-3.5 h-3.5" /></Button>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="flex items-center gap-2">
                                                                        <h4 className="text-[#f8fafc] font-medium truncate">{topic.name}</h4>
                                                                        {topic.is_public && (
                                                                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-[#8b5cf6]/20 text-[#8b5cf6] font-medium flex items-center gap-1">
                                                                                <Globe className="w-3 h-3" /> Public
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-sm text-[#94a3b8] mt-1">{topic.verse_count || 0} verse{(topic.verse_count !== 1) ? 's' : ''}</p>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center justify-end gap-1 mt-2 sm:mt-0 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                                            {renamingTopicId !== topic.id && (
                                                                <>
                                                                    <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${topic.is_public ? 'text-[#8b5cf6] hover:text-[#a78bfa]' : 'text-[#94a3b8] hover:text-[#8b5cf6]'}`}
                                                                        onClick={e => handlePublishToggle(e, topic)} title={topic.is_public ? "Unpublish from Library" : "Publish to Library"}>
                                                                        {topic.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                                                                    </Button>
                                                                    <Button variant="ghost" size="sm" className="text-[#94a3b8] hover:text-[#10b981] hover:bg-[#10b981]/10 h-8 w-8 p-0" onClick={e => startRenameTopic(e, topic)} title="Rename"><PenLine className="w-4 h-4" /></Button>
                                                                    <Button variant="ghost" size="sm" className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10 h-8 w-8 p-0"
                                                                        onClick={e => { e.stopPropagation(); handleDeleteTopic(topic.id); }}><Trash2 className="w-4 h-4" /></Button>
                                                                    <ChevronRight className="w-5 h-5 text-[#475569]" />
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center p-8 text-center text-[#94a3b8]">
                                                <Bookmark className="w-12 h-12 mb-4 opacity-20" />
                                                <p>You haven't created any topics yet.</p>
                                                <p className="text-sm mt-2">Find a verse and click "Add to Topic" to get started.</p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* LIBRARY TAB */}
                    {view === 'list' && activeTab === 'library' && (
                        <div className="space-y-4">
                            <p className="text-sm text-[#94a3b8]">Browse topics published by the community. Add any to your collection to make your own copy.</p>
                            
                            {/* Search bar */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                <Input
                                    value={librarySearch}
                                    onChange={(e) => setLibrarySearch(e.target.value)}
                                    placeholder="Search topics..."
                                    className="bg-[#09090b] border-[#1e293b] text-white pl-9 h-9"
                                />
                            </div>

                            {isLoadingLibrary ? (
                                <div className="flex items-center justify-center p-8"><Loader2 className="w-8 h-8 text-[#8b5cf6] animate-spin" /></div>
                            ) : filteredLibraryTopics.length > 0 ? (
                                <div className="border border-[#1e293b] rounded-md bg-[#09090b]/50 divide-y divide-[#1e293b]">
                                    {filteredLibraryTopics.map(topic => (
                                        <div key={topic.id} className="group p-4 hover:bg-[#1e293b] transition-colors cursor-pointer flex items-center gap-4"
                                            onClick={() => loadLibraryTopicDetails(topic)}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-[#f8fafc] font-medium truncate">{topic.name}</h4>
                                                <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-[#94a3b8]">
                                                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {topic.author_name || 'Anonymous'}</span>
                                                    {topic.published_at && (
                                                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(topic.published_at).toLocaleDateString()}</span>
                                                    )}
                                                    <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-[#94a3b8]" /> {topic.views?.toLocaleString() || 0}</span>
                                                    <span>{topic.verse_count || 0} verse{(topic.verse_count !== 1) ? 's' : ''}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                                {topic.is_own && (
                                                    <span className="text-xs text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded">Your topic</span>
                                                )}
                                                <ChevronRight className="w-5 h-5 text-[#475569]" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : libraryTopics.length > 0 && filteredLibraryTopics.length === 0 ? (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-[#94a3b8] border border-[#1e293b] rounded-md bg-[#09090b]/50">
                                    <Search className="w-10 h-10 mb-3 opacity-20" />
                                    <p>No topics match "{librarySearch}"</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-[#94a3b8] border border-[#1e293b] rounded-md bg-[#09090b]/50">
                                    <Library className="w-12 h-12 mb-4 opacity-20" />
                                    <p>No published topics yet.</p>
                                    <p className="text-sm mt-2">Be the first to publish a topic for others to explore!</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* My Topic detail view */}
                    {view === 'detail' && (
                        <div>
                            {/* Actions for own published topics */}
                            {selectedTopic?.is_public && (
                                <div className="mb-3 flex justify-end gap-2">
                                    <a
                                        href={`/narrative/${selectedTopic.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#10b981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white text-xs font-medium transition-all"
                                    >
                                        <BookOpen className="w-3.5 h-3.5" /> Read as Narrative
                                    </a>
                                    <button
                                        onClick={async () => {
                                            const url = `${window.location.origin}/narrative/${selectedTopic.id}`;
                                            const shareData = {
                                                title: `Quran Narrative: ${selectedTopic?.name}`,
                                                text: `Read this Quranic narrative: ${selectedTopic?.name}`,
                                                url: url,
                                            };
                                            try {
                                                if (navigator.share) {
                                                    await navigator.share(shareData);
                                                } else {
                                                    await navigator.clipboard.writeText(url);
                                                    toast.success('Link copied to clipboard!');
                                                }
                                            } catch (err) {
                                                console.error('Error sharing:', err);
                                            }
                                        }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1e293b] hover:bg-[#334155] text-[#f8fafc] text-xs font-medium transition-colors border border-[#334155]"
                                    >
                                        <Share2 className="w-3.5 h-3.5" /> Share
                                    </button>
                                </div>
                            )}
                            {isLoadingDetails ? (
                                <div className="flex items-center justify-center p-8"><Loader2 className="w-8 h-8 text-[#10b981] animate-spin" /></div>
                            ) : topicVerses.length > 0 ? (
                                <div className="space-y-2">{topicVerses.map((verse, index) => renderVerseCard(verse, index, false))}</div>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-[#94a3b8]">
                                    <Bookmark className="w-12 h-12 mb-4 opacity-20" /><p>No verses in this topic yet.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Library topic detail view (read-only) */}
                    {view === 'library-detail' && (
                        <div>
                            {selectedTopic && (
                                <div className="mb-3 flex items-center justify-between gap-3 bg-[#09090b]/50 p-2 rounded-lg border border-[#1e293b]">
                                    {selectedTopic.is_own && (
                                        <span className="text-xs text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded">Your topic</span>
                                    )}
                                    <div className="flex items-center gap-2 ml-auto">
                                        {!selectedTopic.is_own && (
                                            <Button 
                                                size="sm" 
                                                className="bg-[#8b5cf6] hover:bg-[#7c3aed] text-white h-[34px] px-3 rounded-lg text-xs font-medium gap-1.5 transition-all" 
                                                disabled={isCloning}
                                                onClick={() => handleCloneTopic(selectedTopic.id)}
                                            >
                                                {isCloning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                                                Add to My Topics
                                            </Button>
                                        )}
                                        <a
                                            href={`/narrative/${selectedTopic.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-lg bg-gradient-to-r from-[#10b981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white text-xs font-medium transition-all"
                                        >
                                            <BookOpen className="w-3.5 h-3.5" /> Read as Narrative
                                        </a>
                                        <button
                                            onClick={async () => {
                                                const url = `${window.location.origin}/narrative/${selectedTopic.id}`;
                                                const shareData = {
                                                    title: `Quran Narrative: ${selectedTopic?.name}`,
                                                    text: `Read this Quranic narrative: ${selectedTopic?.name}`,
                                                    url: url,
                                                };
                                                try {
                                                    if (navigator.share) {
                                                        await navigator.share(shareData);
                                                    } else {
                                                        await navigator.clipboard.writeText(url);
                                                        toast.success('Link copied to clipboard!');
                                                    }
                                                } catch (err) {
                                                    console.error('Error sharing:', err);
                                                }
                                            }}
                                            className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-lg bg-[#1e293b] hover:bg-[#334155] text-[#f8fafc] text-xs font-medium transition-colors border border-[#334155]"
                                        >
                                            <Share2 className="w-3.5 h-3.5" /> Share
                                        </button>
                                    </div>
                                </div>
                            )}
                            {isLoadingDetails ? (
                                <div className="flex items-center justify-center p-8"><Loader2 className="w-8 h-8 text-[#8b5cf6] animate-spin" /></div>
                            ) : topicVerses.length > 0 ? (
                                <div className="space-y-2">{topicVerses.map((verse, index) => renderVerseCard(verse, index, true))}</div>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-[#94a3b8]">
                                    <Bookmark className="w-12 h-12 mb-4 opacity-20" /><p>No verses in this topic.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>

        <LoginPromptDialog isOpen={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} featureName="Topics" />
        </>
    );
};

export default TopicManager;
